import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { GetDataError } from 'restful-react'
import {
  ErrorResponse,
  ShipmentIssueAssigneeRole,
  useGetShipmentIssueAssigneeRoles,
} from 'apps/marketplace/apiTypes/shipmentIssues'

export const NO_DEFAULT_OWNER_OPTION = {
  id: 'no-owner-assignee',
  dataTest: 'no-owner-assignee-option',
  label: 'No Default Owner',
  value: '',
}

export type TSelectedOwner = IRoleOption | ShipmentIssueAssigneeRole | undefined

export interface IRoleOption {
  id: string
  dataTest?: string
  label: string
  value: string
  type?: string
  text?: string
}

export interface IUseAssigneeRoles {
  assigneeRoles: ShipmentIssueAssigneeRole[] | undefined
  assigneeRolesOptions: IRoleOption[]
  otherAssignees: IRoleOption[]
  setOtherAssignees: Dispatch<SetStateAction<IRoleOption[]>>
  selectedOwnerObject: TSelectedOwner
  isAssigneeRolesLoading: boolean
  assigneeRolesError: GetDataError<ErrorResponse> | null
}

const useAssigneeRoles = (
  defaultOwnerSelectedId?: string,
  otherAssigneesList = []
): IUseAssigneeRoles => {
  const [rolesHaveBeenSet, setHaveRolesBeenSet] = useState<boolean>(false)
  const [assigneeRolesOptions, setAssigneeRoles] = useState<IRoleOption[]>([])
  const [otherAssignees, setOtherAssignees] = useState<IRoleOption[]>(otherAssigneesList)
  const { data: assigneeRolesResp, error, loading } = useGetShipmentIssueAssigneeRoles({})
  const { shipmentIssueAssigneeRoles = [] } = assigneeRolesResp || {}

  useEffect(() => {
    if (shipmentIssueAssigneeRoles?.length && !rolesHaveBeenSet) {
      const rolesOptions = shipmentIssueAssigneeRoles.map(({ id, role, type }) => ({
        id: `${id}`,
        dataTest: `${id}-option`,
        label: role,
        value: String(id),
        type,
      }))
      setAssigneeRoles(rolesOptions)
      setHaveRolesBeenSet(true)
    }
  }, [rolesHaveBeenSet, shipmentIssueAssigneeRoles])

  const selectedOwnerObject = useMemo(() => {
    let owner: TSelectedOwner
    owner = shipmentIssueAssigneeRoles?.find(({ id }) => String(id) === defaultOwnerSelectedId)
    if (!owner) {
      owner = otherAssignees?.find(({ id }) => String(id) === defaultOwnerSelectedId)
    }
    return owner
  }, [defaultOwnerSelectedId, otherAssignees, shipmentIssueAssigneeRoles])

  return {
    assigneeRoles: assigneeRolesResp?.shipmentIssueAssigneeRoles,
    assigneeRolesOptions,
    otherAssignees,
    setOtherAssignees,
    selectedOwnerObject,
    isAssigneeRolesLoading: loading,
    assigneeRolesError: error,
  }
}

export default useAssigneeRoles
